/** A catalog record's phone preview where it is drawn — the dock's Preview segment (from
 *  1100 px of content) or the header's drawer below that (the catalog records' O5; the tour
 *  plan §4). The render state is the page's (`RecordPreviewProvider`); this is the view of it,
 *  for a record whose phone shows the record itself (an activity, an itinerary) — the tour's
 *  own pane (`TourPreviewPane`) adds its day selection and its notes.
 *
 *  - Nothing renders until Render (on demand only, never per keystroke).
 *  - A reloaded record (its `updated_ts`) or a changed draft (its `version`) makes an earlier
 *    render "Out of date"; Render then applies the draft when there is one the viewer may
 *    render (`canDraft` and changes in it) — else it redraws the saved record. In view mode
 *    the draft is empty, so the phone shows the saved record.
 *  - A required question the draft raises and no one has answered holds Render, as it holds
 *    Save: the pane says to answer it first (`blocked`, the ledger's words).
 *  - `notes`: under the controls — what this render cannot show. */
import { useMemo, useSyncExternalStore, type ReactNode } from 'react';

import { MobilePreviewView } from '@/components/MobilePreview';
import { draftCount } from '@/features/records/draft/changeSet';
import { useDraftStable } from '@/features/records/draft/DraftProvider';

import { useRecordPreview } from './RecordPreviewProvider';

export function RecordPreviewPane({ notes }: { notes?: ReactNode }) {
  const { controller, canDraft, blocked, screen, setScreen, updatedTs } = useRecordPreview();
  const { store } = useDraftStable();
  const version = useSyncExternalStore(store.subscribe, () => store.getState().version);

  // Re-read on every draft change and on a reloaded record (its `updated_ts`).
  // eslint-disable-next-line react-hooks/exhaustive-deps -- version / updatedTs are the inputs of the getter
  const fingerprint = useMemo(() => controller.currentFingerprint(), [controller, version, updatedTs]);
  // A draft Render would apply — not merely the permission to draft.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- recomputed per draft change, read from the store
  const hasDraft = useMemo(() => canDraft && draftCount(store.getState()) > 0, [canDraft, store, version]);

  return (
    <MobilePreviewView
      controller={controller}
      fingerprint={fingerprint}
      hasDraft={hasDraft}
      blocked={blocked}
      {...(screen !== undefined ? { screen } : {})}
      onScreen={setScreen}
      {...(notes ? { notes } : {})}
    />
  );
}
